import { useState } from "react";
import { toast } from "sonner";
import { Scale, Loader2, CheckCircle2, AlertTriangle, PauseCircle, RotateCcw, X, Layers } from "lucide-react";
import { getCraneWeight, inwardWeighed } from "@/lib/api";
import { fmtCoilKg } from "@/lib/utils";

const kg = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

const Detail = ({ label, value, testId }) => (
  <div className="min-w-0">
    <p className="text-[11px] text-slate-500">{label}</p>
    <p data-testid={testId} className="text-base font-semibold text-slate-900 break-words">{value || "—"}</p>
  </div>
);

// Step 2–4: verify details → crane weight → auto-save within tolerance, else "Received – Hold".
export const WeighPanel = ({ info, onDone, onCancel, onBundle }) => {
  const { coil, expected_gross_kg: expected, expected_basis: basis, tolerance_kg: tol, tolerance_source: tolSrc } = info;
  const isSfg = coil.kind === "sfg";
  const [reading, setReading] = useState(null); // {weight, diff, within}
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // "received" | "received_hold"

  const save = async (weight, hold) => {
    setSaving(true);
    try {
      const r = await inwardWeighed(coil.coil_id, weight, hold);
      setSaved(r.coil.status);
      toast.success(hold ? `${coil.coil_id} kept as Received – Hold` : `${coil.coil_id} saved to inventory`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const weigh = async () => {
    setFetching(true);
    setReading(null);
    try {
      const s = await getCraneWeight();
      if (!s.online || s.weight_kg == null) { toast.error(s.hint || "Crane scale is not sending readings"); return; }
      const diff = +(s.weight_kg - expected).toFixed(2);
      const within = Math.abs(diff) <= (tol || 0);
      setReading({ weight: s.weight_kg, diff, within });
      setTimeout(() => document.querySelector('[data-testid="weigh-result"]')?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
      if (within) await save(s.weight_kg, false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read the crane scale");
    } finally {
      setFetching(false);
    }
  };

  const locked = saved !== null;
  return (
    <div data-testid="weigh-panel" className="bg-white rounded-3xl border border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span data-testid="weigh-status-pill" className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
            saved === "received" ? "bg-green-100 text-green-800" : saved === "received_hold" ? "bg-red-100 text-red-800" : isSfg ? "bg-indigo-100 text-indigo-800" : "bg-amber-100 text-amber-800"}`}>
            {isSfg ? "SFG" : "RM"} · {saved === "received" ? "Received" : saved === "received_hold" ? "Received – Hold" : "Pending inward"}
          </span>
          <h3 data-testid="weigh-coil-id" className="mt-1 text-3xl font-bold tracking-tight text-slate-900 font-mono-data">{coil.coil_id}</h3>
        </div>
        {!locked && (
          <button data-testid="weigh-cancel" onClick={onCancel} className="h-11 w-11 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" title="Cancel">
            <X className="h-5 w-5 text-slate-600" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-2">
        <Detail label="Supplier" value={coil.supplier} testId="weigh-supplier" />
        <Detail label={isSfg ? "SKU" : "Grade"} value={isSfg ? (coil.sku_name || coil.grade) : coil.grade} testId="weigh-grade" />
        <Detail label="Thickness" value={coil.thickness_mm != null ? `${coil.thickness_mm} mm` : ""} testId="weigh-thickness" />
        <Detail label="Width" value={coil.width_mm != null ? `${coil.width_mm} mm` : ""} testId="weigh-width" />
        <Detail label="Net weight" value={`${fmtCoilKg(coil.kind, coil.weight_kg)} kg`} testId="weigh-net" />
        <Detail label="Pack list #" value={coil.packaging_list_number} testId="weigh-packlist" />
        <Detail label="Supplier coil ID" value={coil.supplier_coil_id} testId="weigh-supplier-coil" />
        <Detail label="Vehicle" value={coil.vehicle_number} testId="weigh-vehicle" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-500">Expected gross weight{basis === "net" && " (no gross on tag — using net)"}</p>
          <p data-testid="weigh-expected" className="text-2xl font-bold whitespace-nowrap text-slate-900 font-mono-data">{kg(expected)} <span className="text-xl text-slate-400">kg</span></p>
        </div>
        <div className={`rounded-2xl px-4 py-3 ${tol == null ? "bg-amber-50" : "bg-sky-50"}`}>
          <p className="text-sm text-slate-500">Allowed difference</p>
          {tol == null ? (
            <p data-testid="weigh-tolerance" className="text-base font-semibold text-amber-800 mt-2 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0" /> No {isSfg ? "SFG" : "RM"} tolerance set (Masters → {isSfg ? "SFG SKU" : "RM Grade"}) — must match exactly
            </p>
          ) : (
            <p data-testid="weigh-tolerance" className="text-2xl font-bold whitespace-nowrap text-sky-900 font-mono-data">± {kg(tol)} <span className="text-xl text-sky-400">kg</span>
              <span className="block text-xs font-sans font-medium text-sky-700 mt-1">for {tolSrc}</span></p>
          )}
        </div>
      </div>

      {!locked && (
        <button data-testid="get-crane-weight-btn" onClick={weigh} disabled={fetching || saving}
          className="w-full h-14 rounded-2xl bg-slate-900 text-white text-lg font-semibold inline-flex items-center justify-center gap-3 hover:bg-slate-800 active:scale-[0.99] disabled:opacity-60 transition-[background-color,transform]">
          {fetching ? <Loader2 className="h-7 w-7 animate-spin" /> : reading ? <RotateCcw className="h-7 w-7" /> : <Scale className="h-7 w-7" />}
          {fetching ? "Reading crane scale…" : reading ? "Get gross weight again" : "Get gross weight from crane scale"}
        </button>
      )}

      {!locked && isSfg && onBundle && !reading && (
        <button data-testid="weigh-as-bundle-btn" onClick={onBundle}
          className="w-full h-12 rounded-2xl bg-indigo-50 border-2 border-indigo-200 text-indigo-900 text-base font-semibold inline-flex items-center justify-center gap-2 hover:bg-indigo-100 transition-colors">
          <Layers className="h-5 w-5" /> Mixed with other coils? Weigh as bundle
        </button>
      )}

      {reading && (
        <div data-testid="weigh-result" data-within={reading.within}
          className={`rounded-2xl px-5 py-4 border-2 ${reading.within ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 items-center">
            <div>
              <p className="text-sm text-slate-600">Crane scale gross</p>
              <p data-testid="weigh-crane" className="text-4xl font-bold whitespace-nowrap font-mono-data text-slate-900">{kg(reading.weight)} <span className="text-2xl text-slate-400">kg</span></p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Difference</p>
              <p data-testid="weigh-diff" className={`text-3xl xl:text-4xl font-bold whitespace-nowrap font-mono-data ${reading.within ? "text-green-700" : "text-red-700"}`}>
                {reading.diff > 0 ? "+" : ""}{kg(reading.diff)} kg
              </p>
            </div>
            <div className="sm:col-span-2 xl:col-span-1 xl:text-right">
              {reading.within ? (
                <p data-testid="weigh-status-ok" className="inline-flex items-center gap-2 text-xl font-bold text-green-700">
                  {saving ? <Loader2 className="h-7 w-7 animate-spin" /> : <CheckCircle2 className="h-8 w-8" />} {saving ? "Saving…" : "Within tolerance"}
                </p>
              ) : (
                <p data-testid="weigh-status-out" className="inline-flex items-center gap-2 text-xl font-bold text-red-700"><AlertTriangle className="h-8 w-8" /> Out of tolerance</p>
              )}
            </div>
          </div>
          {!reading.within && !locked && (
            <button data-testid="received-hold-btn" onClick={() => save(reading.weight, true)} disabled={saving}
              className="mt-3 w-full h-14 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-xl font-semibold inline-flex items-center justify-center gap-3 disabled:opacity-60 transition-colors">
              {saving ? <Loader2 className="h-6 w-6 animate-spin" /> : <PauseCircle className="h-6 w-6" />} Received – Hold
            </button>
          )}
        </div>
      )}

      {locked && (
        <div data-testid="weigh-saved" className={`rounded-2xl px-5 py-3 text-base font-semibold flex items-center gap-3 ${saved === "received" ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
          {saved === "received" ? <CheckCircle2 className="h-7 w-7" /> : <PauseCircle className="h-7 w-7" />}
          {saved === "received" ? "Saved to inventory as Received" : "Saved as Received – Hold. A Director must release it."}
        </div>
      )}
    </div>
  );
};
